import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import Label from "../label";
import TextInput from "../textInput";
import GlobalButton from "../button";
import { handleOpenSnackBar } from "../../utils/helper";
import { createDuplicateContract } from "../../services/ContractApi";
import { set } from "date-fns";
import AppLoader from "../loader";
import { useNavigate } from "react-router-dom";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    // width: "600px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

const cliData = [
  {
    mobileNo: "",
    cliNumber: true,
    line: 1,
    legacyTariff: {
      title: "Legacy",
      isSelected: false,
      type: "legacyTariff",
    },
    digitalTariff: {
      title: "Digital/NGN",
      isSelected: true,
      type: "digitalTariff",
    },
    additionalService: {
      title: "Additional  services",
      isSelected: false,
      type: "additionalService",
    },
    differentAddress: {
      title: "Different Address",
      isSelected: true,
      type: "differentAddress",
      value: "",
    },
    upgradeCharge: {
      title: "Install",
      isSelected: false,
      type: "upgradeCharge",
      salePrice: 60,
      buyPrice: 60,
      attachedValue: "",
      timePeriod: "",
      upPayment: true,
      isCollected: "",
    },
    migration: {
      title: "Migration",
      isSelected: false,
      type: "migration",
      salePrice: 60,
      buyPrice: 60,
      attachedValue: "",
      timePeriod: "",
      upPayment: false,
      firstMonth: false,
      isCollected: "",
    },
    transfer: {
      title: "Transfer",
      isSelected: false,
      type: "transfer",
      attachedValue: "",
    },

    lineValues: [
      {
        legacy: null,
        legacyService1: null,
        legacyService2: null,
        legacyService3: null,
        legacyService4: null,
        ngnValue: null,
        ngnService1: null,
        ngnService2: null,
        ngnService3: null,
        ngnService4: null,
      },
    ],
  },
];

const DuplicateCountDialog = forwardRef(({}, ref) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [duplicateCount, setDuplicateCount] = useState(0);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setDialogData(params.data);
      setOpen(true);
    },
    disable() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleGenerate = async () => {
    if (duplicateCount <= 0) {
      handleOpenSnackBar("error", "Please enter a valid count");
      return;
    }
    if (duplicateCount > 10) {
      handleOpenSnackBar("error", "Max generation limit is 6");
      return;
    }

    const generalInfo = {
      signupDate: new Date(),
      agent: dialogData.agent,
      name: dialogData.name,
      businessName: dialogData.businessName,
      designation: dialogData.designation,
      title: dialogData.title,
      email: dialogData.email,
      businessType: dialogData.businessType,
      mobileNo: dialogData.mobileNo,
      currentProvider: dialogData.currentProvider,
      liveDate: null,
      secondName: "",
      isAddCustomer: false,
      address1: "",
      address2: "",
      postCode: "",
      city: "",
      town: "",
      billingEmail: "",
      eBilling: "",
    };

    const payload = {
      duplicateCount: duplicateCount,
      contractData: {
        linkedContract: dialogData?.contractId,
        status: "envelope-not-sent",
        agentId: dialogData?.agentId,
        generalInfo,
        note: "",
        isBillingEmailSame: false,
        contractLength: {},
        cliData: cliData,
        downloadSpeed: { min: 0, max: 0 },
        uploadSpeed: { min: 0, max: 0 },
        routerValue: {
          value: 0,
          price: 0,
          costType: "oneOfCost",
          isUpgrade: false,
        },
        cordedPhone: {
          value: 0,
          price: 0,
          costType: "oneOfCost",
        },
        cordedlessPhone: {
          value: 0,
          price: 0,
          costType: "oneOfCost",
        },
        equipments: {
          value: "",
          type: "",
          price: 0,
          costType: "oneOfCost",
        },
        paperBillingPrice: {
          value: "Paper Billing Charges",
          salePrice: 5.0,
          buyPrice: 5.0,
          highPrice: 5.0,
          length12: 2.0,
          length18: 2.0,
          length24: 2.0,
          length36: 2.0,
          error: false,
          errorMessage: "",
        },
        eBillingAddress: {
          town: "",
          city: "",
          postCode: "",
          address1: "",
          address2: "",
        },
        isDebitDetails: false,
        debitDetails: {
          yes: false,
          no: false,
        },
      },
    };

    try {
      setIsLoading(true);
      const response = await createDuplicateContract(payload);
      if (response?.data?.status === "success") {
        navigate("/all-prospects");
        handleOpenSnackBar(
          "success",
          "Duplicate contract generated successfully"
        );
        handleClose();
      } else {
        setDuplicateCount(0);
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <AppLoader isVisible={isLoading} />

      <Stack
        p={2}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography variant="h5" fontWeight={500}>
          Generate Duplicate Prospects
        </Typography>
      </Stack>
      <DialogContent dividers>
        <Box>
          <Label title={"Enter count"} htmlFor={"id"} />
          <TextInput
            id={"count"}
            type={"number"}
            value={duplicateCount}
            onChange={(e) => setDuplicateCount(e.target.value)}
            placeholder={"Enter count"}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction={"row"} spacing={2}>
          <GlobalButton
            title={"cancel"}
            handleClickBtn={handleClose}
            style={{
              height: 30,
              borderRadius: 1,
            }}
          />
          <GlobalButton
            variant={"custom_gradient"}
            handleClickBtn={handleGenerate}
            title={"Generate"}
            style={{
              height: 30,
              borderRadius: 1,
            }}
          />
        </Stack>
      </DialogActions>
    </BootstrapDialog>
  );
});

export default DuplicateCountDialog;
